import React, {
  PropsWithChildren,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  isValidElement,
  cloneElement,
  ReactElement,
  HTMLAttributes,
} from 'react';
import styled from '@emotion/styled';
import { Divider } from '../divider';
import { Stack, StackProps } from '../stack';
import { ButtonProps } from '../button';
import { buildCSSWithLength, CSSValueWithLength, getZIndex, space, SpaceProps } from '../../styles';
import { useResize } from '../../hooks/useResize';
import { ModalOverlay, ModalOverlayBaseProps, ModalBaseCancelReason, SAFE_TOP_BOTTOM_AREA } from './ModalOverlay';
import { FloatingTreeContext } from '../context';
import { useProviderConfig } from '../provider';
import { ModalActionButton, ModalActionButtonType } from './ModalActionButton';
import { ModalHeader } from './ModalHeader';
import { border_radius_styles_with_size } from '../../foundation';

export type ModalButtonProps = Omit<ButtonProps, 'onClick'>;
export type ModalCancelReason = ModalBaseCancelReason;

/**
 * 콘텐츠 영역 커스터마이징
 *
 * - SpaceProps: margin, padding 등 여백 조정 가능
 * - maxHeight: 최대 높이 지정 (미지정 시 안전여백 내 자동 계산)
 * - scrollable: true 시 overflow-y: auto 적용
 */
export interface ModalContentProps extends SpaceProps, HTMLAttributes<HTMLDivElement> {
  maxHeight?: CSSValueWithLength;
  scrollable?: boolean;
}

type LayoutHeightType = {
  header: number;
  footer: number;
};

export interface ModalProps extends Omit<ModalOverlayBaseProps, 'onClose'> {
  title?: ReactNode;
  confirmText?: string;
  confirmButtonProps?: ModalButtonProps;
  cancelText?: string;
  cancelButtonProps?: ModalButtonProps;
  /**
   * autofocus 지정할 기본 버튼 요소
   * @default null
   */
  autoFocusButton?: ModalActionButtonType | null;
  /**
   * 헤더 영역 타이틀 좌측 backButton 사용 유무
   * @default false
   */
  backButton?: boolean;
  /** @default true */
  closeButton?: boolean;
  /**
   * 좁은 간격 사용 유무
   * @default false
   */
  dense?: boolean;
  contentProps?: ModalContentProps;
  /**
   * - 기본 Header(title, backButton, closeButton)는 미노출됩니다.
   */
  header?: ReactNode;
  /**
   * - 기본 Footer(confirmText, cancelText)는 미노출됩니다.
   * - dense 속성이 적용되지 않습니다.
   * - fullScreen 속성이 true일 경우 자동 해당 영역은 fixed 처리 됩니다.
   */
  footer?: ReactNode;
  /**
   * 푸터 버튼 좌측 영역에 콘텐츠를 추가할 수 있습니다.
   */
  footerExtra?: ReactNode;
  /**
   * 전체화면 사용 유무, 헤더/푸터 영역이 자동 고정됩니다.
   * @default false
   */
  fullScreen?: boolean;
  onConfirm?: () => void;
  /**
   * reason을 포함한 cancel 이벤트를 전달합니다.
   *  - 'cancelClick' | 'escapeKeyPress' | 'overlayClick'
   */
  onCancel?: (reason: ModalCancelReason) => void;
  /**
   * Modal Header backButton 클릭 이벤트
   */
  onClickBackButton?: () => void;
}

export const MIN_WIDTH = 480;

export const Modal = FloatingTreeContext(
  ({
    opened: openProp,
    children,
    title,
    width = MIN_WIDTH,
    zIndex = getZIndex('modal'),
    confirmText,
    cancelText,
    closeButton = true,
    backButton = false,
    autoFocusButton = null,
    dense,
    fill,
    fullScreen = false,
    header,
    footer,
    confirmButtonProps,
    cancelButtonProps,
    contentProps,
    footerExtra,
    onConfirm,
    onCancel,
    onExited,
    onClickBackButton,
    ...props
  }: PropsWithChildren<ModalProps>) => {
    const { components: { modal: contextModal } = { modal: { dense: false, fill: false } } } = useProviderConfig();
    const mergedDense = useMemo(() => dense ?? contextModal?.dense, [dense, contextModal?.dense]);
    const mergedFill = useMemo(() => fill ?? contextModal?.fill, [fill, contextModal?.fill]);
    const { windowWidth, windowHeight } = useResize();
    const [contentOverflow, setContentOverflow] = useState<boolean>(false);
    const [layoutHeight, setLayoutHeight] = useState<LayoutHeightType>({
      header: 0,
      footer: 0,
    });

    const layoutStyles: StackProps = {
      p: mergedDense ? 20 : 24,
      gap: 8,
    };
    const hasFooter = Boolean(footer || confirmText || cancelText);

    const handleResize = (height: number, target: keyof LayoutHeightType) => {
      setLayoutHeight((prevLayoutHeight) => ({ ...prevLayoutHeight, [target]: height }));
    };

    const headerRef = useCallback(
      (node: HTMLElement | null) => {
        handleResize(node?.offsetHeight ?? 0, 'header');
      },
      [windowWidth, windowHeight, openProp],
    );

    const footerRef = useCallback(
      (node: HTMLElement | null) => {
        handleResize(node?.offsetHeight ?? 0, 'footer');
      },
      [windowWidth, windowHeight, openProp],
    );

    const contentRef = useCallback(
      (node: HTMLElement | null) => {
        const scrollHeight = node?.scrollHeight ?? 0;
        const offsetHeight = node?.offsetHeight ?? 0;
        setContentOverflow(scrollHeight > offsetHeight);
      },
      [windowWidth, windowHeight, openProp],
    );

    return (
      <ModalOverlay
        width={width}
        fill={mergedFill}
        fullScreen={fullScreen}
        opened={openProp}
        onExited={onExited}
        onCancelEventCallback={(event) => onCancel?.(event as ModalCancelReason)}
        zIndex={zIndex}
        controlledOpen
        autoFocus={autoFocusButton !== null}
        {...props}
      >
        {header
          ? isValidElement(header) &&
            cloneElement(header as ReactElement, {
              ref: headerRef,
            })
          : title && (
              <ModalLayout ref={headerRef}>
                <Stack {...layoutStyles}>
                  <ModalHeader
                    title={title}
                    backButton={backButton}
                    closeButton={closeButton}
                    onClickBackButton={onClickBackButton}
                    onCancel={() => onCancel?.('cancelClick')}
                  />
                </Stack>
                <Divider />
              </ModalLayout>
            )}
        {children && (
          <ModalContent
            ref={contentRef}
            fullScreen={fullScreen}
            hasFooter={hasFooter}
            layoutHeight={layoutHeight}
            contentOverflow={contentOverflow}
            p={layoutStyles.p}
            {...contentProps}
          >
            {children}
          </ModalContent>
        )}
        {footer
          ? isValidElement(footer) &&
            cloneElement(footer as ReactElement, {
              ref: footerRef,
            })
          : (confirmText || cancelText) && (
              <ModalLayout ref={footerRef}>
                <Divider />
                <Stack align='center' justify={footerExtra ? 'space-between' : 'flex-end'} p={layoutStyles.p}>
                  {footerExtra && (
                    <Stack width='100%' gap={8} align='center'>
                      {footerExtra}
                    </Stack>
                  )}
                  <ModalButtonContainer width={footerExtra ? undefined : '100%'}>
                    {cancelText && (
                      <ModalActionButton
                        autoFocus={autoFocusButton === 'cancel'}
                        onClick={() => onCancel?.('cancelClick')}
                        {...cancelButtonProps}
                      >
                        {cancelText}
                      </ModalActionButton>
                    )}
                    {confirmText && (
                      <ModalActionButton
                        autoFocus={autoFocusButton === 'confirm'}
                        kind='primary'
                        onClick={onConfirm}
                        {...confirmButtonProps}
                      >
                        {confirmText}
                      </ModalActionButton>
                    )}
                  </ModalButtonContainer>
                </Stack>
              </ModalLayout>
            )}
      </ModalOverlay>
    );
  },
);
export const ModalContent = styled.div<
  ModalContentProps & {
    fullScreen: boolean;
    hasFooter: boolean;
    layoutHeight: LayoutHeightType;
    contentOverflow: boolean;
  }
>`
  ${({ contentOverflow, scrollable }) => (contentOverflow || scrollable) && `overflow-y: auto`};
  ${({ fullScreen, hasFooter }) =>
    !fullScreen &&
    !hasFooter &&
    `
      border-bottom-left-radius: ${border_radius_styles_with_size.large}px;
      border-bottom-right-radius: ${border_radius_styles_with_size.large}px;
    `};
  ${({ fullScreen, layoutHeight, maxHeight }) => {
    const { header, footer } = layoutHeight;
    return fullScreen
      ? `
        flex: 1;
        overflow-y: auto
      `
      : maxHeight
      ? buildCSSWithLength('max-height', maxHeight)
      : `max-height: calc(100vh - ${SAFE_TOP_BOTTOM_AREA + header + footer}px)`;
  }};
  ${space}
`;

const ModalLayout = styled.div`
  width: 100%;
`;

const ModalButtonContainer = styled(Stack)`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
